"use client";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ShareIcon from "@mui/icons-material/Share";
import { useState } from "react";
import CopyButton from "~/components/CopyButton";
import Link from "~/components/Link";
import { Loading } from "~/components/loading";
import { useSnackbar } from "~/components/snackbar";
import { songSharingService } from "~/connectors/songSharing.connector";
import type { ShareCodeResponse } from "~/types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAudioMiniPlayerState } from "./audio/useAudio";

interface ShareSongDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  darkMode?: boolean;
}

export function ShareSongDialog({
  open,
  onClose,
  orderId,
  darkMode,
}: ShareSongDialogProps) {
  const [shareResponse, setShareResponse] = useState<ShareCodeResponse | null>(
    null,
  );
  const { setBigPlayerOpen } = useAudioMiniPlayerState();
  const [loading, setLoading] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const handleClose = () => {
    setLoading(true);
    setShareResponse(null);
    setRecipientEmail("");
    onClose();
  };

  // Generate share code on dialog open
  const handleDialogOpen = async () => {
    if (shareResponse) return; // Already loaded

    setLoading(true);
    try {
      const response = await songSharingService.getShareUrl(orderId);
      setShareResponse(response);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Eroare la generarea codului",
      );
    } finally {
      setLoading(false);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    if (!recipientEmail) {
      showError("Te rog introdu o adresă de email");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      showError("Adresa de email nu este validă");
      return;
    }

    setEmailSending(true);
    try {
      await songSharingService.sendGiftEmail(orderId, recipientEmail);
      showSuccess("Email trimis cu succes!");
      setRecipientEmail(""); // Clear email field
      handleClose();
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Eroare la trimiterea email-ului",
      );
    } finally {
      setEmailSending(false);
    }
  };

  const canNativeShare =
    typeof navigator !== "undefined" && "share" in navigator;

  const handleNativeShare = async () => {
    if (!canNativeShare) {
      showError("Partajare nativă nu este suportată de acest browser.");
      return;
    }

    const url = shareResponse?.shareUrl;
    if (!url) {
      showError("Link-ul nu este generat încă");
      return;
    }

    try {
      await navigator.share({
        title: `${shareResponse?.songName ?? "Melodia ta"} 🎵`,
        text: "Ascultă cântecelul personalizat!",
        url,
      });
      showSuccess("Distribuit cu succes!");
    } catch (err: any) {
      if (err && err.name !== "AbortError") {
        showError(err?.message || "Eroare la partajare");
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        className={darkMode ? "dark" : ""}
        slotProps={{
          transition: { onEnter: handleDialogOpen },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CardGiftcardIcon />
              <span>Trimite Cadou Acest Cântecel</span>
            </Box>
            <IconButton aria-label="close" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* NOTE: native share button moved to the input's adornment */}
        </DialogTitle>

        <DialogContent>
          {/* Link cadou */}
          <Box sx={{ my: 1 }}>
            <Typography sx={{ mb: 2 }}>Trimite folosind un link</Typography>
            <TextField
              value={shareResponse?.shareUrl ?? ""}
              fullWidth
              disabled={loading}
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      {loading ? (
                        <Loading size={20} />
                      ) : (
                        <>
                          <CopyButton
                            value={shareResponse?.shareUrl ?? ""}
                            title="Copiază link"
                          />
                          <IconButton
                            aria-label="Distribuie"
                            size="small"
                            onClick={handleNativeShare}
                            disabled={
                              !canNativeShare ||
                              loading ||
                              !shareResponse?.shareUrl
                            }
                            sx={{ ml: 0.5 }}
                          >
                            <ShareIcon />
                          </IconButton>
                        </>
                      )}
                    </InputAdornment>
                  ),
                },
                inputLabel: { shrink: true },
              }}
            />
          </Box>

          {/* Email Section */}
          <Box sx={{ my: 2 }}>
            <Typography sx={{ mb: 2 }}>
              Sau trimite direct un email cu link-ul cadou
            </Typography>
            <Box sx={{ display: "flex" }}>
              <TextField
                label="Email destinatar"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                type="email"
                fullWidth
                size="small"
                disabled={loading}
                slotProps={{
                  input: {
                    sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                  },
                }}
              />
              <Button
                variant={recipientEmail ? "contained" : "outlined"}
                onClick={handleSendEmail}
                disabled={emailSending || !recipientEmail || loading}
                startIcon={<EmailIcon />}
                sx={{
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: "none",
                }}
              >
                Trimite
              </Button>
            </Box>
          </Box>

          {/* QR / Gift card section */}
          <Box sx={{ my: 2 }}>
            <Typography sx={{ mb: 2 }}>
              Sau personalizează o felicitare cu QR
            </Typography>
            <Link
              button
              href={`/felicitare/${orderId}`}
              variant="outlined"
              color="inherit"
              startIcon={<QrCode2Icon />}
              onClick={() => {
                handleClose();
                setBigPlayerOpen(false); // Ensure big player is closed when navigating to the gift card page
              }}
            >
              Creează Felicitarea
            </Link>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={handleClose}>Închide</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
